import { useEffect, useMemo, useRef } from 'react';
import { GridApi, GridOptions } from 'ag-grid-community';
import { AnyRow, GroupingColumn, UseControlledAGGridProps } from '../types';
import {
  AG_GRID_AUTO_COLUMN_ID,
  defaultComparator,
  getOnSortChanged,
  noopComparator,
} from '../utils';

export const useSortable = <
  RowData,
  FullWidthRowData = never,
  RowId extends string = string,
>(
  props: UseControlledAGGridProps<RowData, FullWidthRowData, RowId>,
  api: GridApi | null,
  groupingColumn: GroupingColumn<RowData, RowId> | undefined
) => {
  const sortable = props.sortable;
  const sortableEnabled = sortable !== undefined;
  const groupingColumnId = groupingColumn ? String(groupingColumn.id) : null;
  const sortableRef = useRef(sortable);
  useEffect(() => {
    sortableRef.current = sortable;
  }, [sortable]);
  const sortableColumn = useMemo(
    () => (sortable && sortable.descriptor ? sortable.descriptor.column : null),
    [sortable]
  );
  const sortableDirection = useMemo(
    () =>
      sortable && sortable.descriptor ? sortable.descriptor.direction : null,
    [sortable]
  );
  const sortableDescriptor = useMemo(
    () => ({
      colId: sortableColumn,
      sort: sortableDirection,
    }),
    [sortableColumn, sortableDirection]
  );

  useEffect(() => {
    if (!api || !sortableEnabled) {
      return;
    }

    // With tree-data grouping, AG Grid hides the consumer-defined grouping
    // column and renders a synthetic 'ag-Grid-AutoColumn' in its place — so
    // sort state must target the auto column to take effect.
    const agGridColId =
      sortableDescriptor.colId && sortableDescriptor.colId === groupingColumnId
        ? AG_GRID_AUTO_COLUMN_ID
        : sortableDescriptor.colId;

    const state =
      agGridColId && sortableDescriptor.sort
        ? [{ colId: agGridColId, sort: sortableDescriptor.sort }]
        : undefined;

    api.applyColumnState({ state, defaultState: { sort: null } });
  }, [api, sortableDescriptor, sortableEnabled, groupingColumnId]);

  const sortingGridProps:
    | GridOptions<AnyRow<RowData, FullWidthRowData, RowId>>
    | undefined = sortable
    ? {
        onSortChanged: getOnSortChanged<RowData, FullWidthRowData, RowId>(
          sortableRef,
          groupingColumnId
        ),
        defaultColDef: {
          comparator: sortable.externallySorted
            ? noopComparator
            : defaultComparator,
        },
      }
    : undefined;

  return {
    sortableDescriptor: sortableEnabled ? sortableDescriptor : null,
    sortingGridProps,
  };
};
